/** @jsxImportSource @emotion/react */
import { EuiSkeletonText, EuiText } from "@inscopix/ideas-eui";
import {
  FileBrowser,
  FileBrowserFile,
  FileBrowserProps,
} from "../FileBrowser/FileBrowser";
import { isNonNull } from "utils/isNonNull";
import assert from "assert";
import { memo, ReactNode, useEffect, useMemo, useState } from "react";
import { DropZoneBasic } from "components/DropZoneBasic/DropZoneBasic";
import { useDragState } from "hooks/useDragState";
import { css } from "@emotion/react";
import { uuid } from "utils/uuid";
import { useFlyoutFilesGdtQuery } from "graphql/_Types";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";
import { useUploadFile } from "hooks/useUploadFile";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { isNonNullish } from "utils/isNonNullish";
import { FileSource, FileStatus } from "types/constants";
import { CallOutError } from "components/CallOutError/CallOutError";
import { CellValueRendererFile } from "pages/gdt/cell-value-renderers/CellValueRendererFile";
import { useGetFileBrowserActions } from "./useGetFileBrowserActions";
import { ProjectLayoutContextValue } from "components/Project/ProjectLayoutProvider";
import { ModalArchiveDrsFile } from "components/ModalArchiveDrsFile/ModalArchiveDrsFile";
import { ModalDownloadFiles } from "components/ModalDownloadFiles/ModalDownloadFiles";
import { ModalRenameDrsFile } from "components/ModalRenameDrsFile/ModalRenameDrsFile";
import { ModalUnarchiveDrsFiles } from "components/ModalUnarchiveDrsFile/ModalUnarchiveDrsFile";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { ModalConfirmDestructiveAction } from "components/ModalConfirmDestructiveAction/ModalConfirmDestructiveAction";
import ModalCreateSeries, {
  validateSeriesFiles,
} from "components/ModalCreateSeries/ModalCreateSeries";
import { ModalBreakSeries } from "components/ModalBreakSeries/ModalBreakSeries";
import { ModalDeleteDrsFile } from "components/ModalDeleteDrsFile/ModalDeleteDrsFile";
import { FlyoutDrsFileInfoGenericAssignments } from "components/FlyoutDrsFileInfo/FlyoutDrsFileInfoGeneric";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { useFileUploadContext } from "stores/upload/FileUploadProvider";
import { debounce } from "lodash";
import { useProjectPermissionStore } from "pages/project/ProjectPermissionProvider";

const fileBrowserZIndex = 100;
const styles = {
  mainFlexContainer: css`
    height: 100%;
    min-height: 0px;
    flex: 4;
    position: relative;
  `,
  dropZone: css`
    display: flex;
    z-index: ${fileBrowserZIndex * 2};
    border: 1px dashed lightgray;
    height: 100%;
    width: 100%;
    background-color: white;
    border-radius: 5px;
    align-items: center;
    justify-content: center;
  `,
  fileBrowser: css`
    z-index: ${fileBrowserZIndex};
  `,
};

const extraColumnProps: FileBrowserProps<
  FileBrowserFile,
  "source" | "assignment"
>["extraColumnProps"] = {
  source: {
    cellRenderer: ({ file }) => {
      switch (file.source) {
        case FileSource.UPLOADED:
          return "Uploaded";
        case FileSource.ANALYSIS_RESULT:
          return "Analysis Result";
        case FileSource.UNKNOWN:
          return "";
      }
    },
    sortValueGetter: ({ source }) => source,
    // TODO: port detail renderer FileBrowserDetailRendererAnalysisResult
    // This should be very straightforward but GDT models are locked in GraphQL currently
  },
  assignment: {
    cellRenderer: ({ file }) => (
      <FlyoutDrsFileInfoGenericAssignments fileId={file.id} />
    ),
    sortValueGetter: () => null,
  },
};

const extraColumns: FileBrowserProps<
  FileBrowserFile,
  "source" | "assignment"
>["extraColumns"] = [
  {
    id: "source",
    displayAsText: "Source",
    isSearchable: false,
    schema: "source",
  },
  {
    id: "assignment",
    displayAsText: "References",
    isSearchable: false,
    schema: "text",
  },
];

export interface FileBrowserGdtProps {
  projectKey: string;
}

export const FileBrowserGdt = memo(function FileBrowserGdt({
  projectKey,
}: FileBrowserGdtProps) {
  const hasEditPermission = useProjectPermissionStore(
    (s) => s.permissions.edit,
  );
  const [modal, setModal] = useState<ReactNode>(null);
  const tenantId = useTenantContext((s) => s.currentTenant.id);
  const fileMap = useDataTableContext((s) => s.fileMap);
  const createSeries = useDataTableContext((s) => s.createSeries);
  const breakSeries = useDataTableContext((s) => s.breakSeries);
  const unassignFile = useDataTableContext((s) => s.unassignFile);
  const deleteFile = useDataTableContext((s) => s.deleteFile);
  const numUploadingFiles = useFileUploadContext(
    (s) => Object.keys(s.filesById).length,
  );
  const filesById = useFileUploadContext((s) => s.filesById);

  const baseActions = useGetFileBrowserActions(
    (modal: Parameters<ProjectLayoutContextValue["openModal"]>[0]) => {
      switch (modal.type) {
        case "archiveDrsFile":
          setModal(
            <ModalArchiveDrsFile
              {...modal.props}
              onClose={() => setModal(null)}
            />,
          );
          break;
        case "downloadFiles":
          setModal(
            <ModalDownloadFiles
              {...modal.props}
              onClose={() => setModal(null)}
            />,
          );
          break;
        case "renameDrsFile":
          setModal(
            <ModalRenameDrsFile
              {...modal.props}
              onClose={() => setModal(null)}
            />,
          );
          break;
        case "unarchiveDrsFile":
          setModal(
            <ModalUnarchiveDrsFiles
              {...modal.props}
              onClose={() => setModal(null)}
            />,
          );
          break;
        default:
          break;
      }
    },
  );

  const { loading, data, refetch } = useFlyoutFilesGdtQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      projectKey,
      tenantId,
    },
  });

  // Refetch files when uploads are initiated outside the file browser
  const debouncedRefetch = useMemo(() => debounce(refetch, 100), [refetch]);
  useEffect(() => {
    void debouncedRefetch();
  }, [debouncedRefetch, numUploadingFiles]);

  const fileBrowserFiles: FileBrowserFile[] | undefined = useMemo(() => {
    const filesData = data?.project?.files.nodes;
    if (filesData === undefined) {
      return;
    }
    return (
      filesData
        /**
         * TODO
         * This is a temporary frontend filter to substitute for what was previously
         * the activeFiles computed column. Making this a query level filter disconnects the
         * list of files from the existing file caching when uploading.
         *
         * A frontend filter is the simplest solution right now until we figure out the long-term
         * solution for project file caching in GDTs
         */
        // no deleted files, etc
        .filter((file) => {
          // Remove files with invalid statuses
          switch (file.status) {
            // Filter out deleted files
            case FileStatus["SCHEDULE_DELETE"]:
            case FileStatus["SCHEDULE_DATA_DELETE"]:
            case FileStatus["DATA_DELETED"]:
            case FileStatus["UPLOAD_CANCELLED"]:
              return false;
            case FileStatus["CREATED"]:
            case FileStatus["UPLOADING"]:
            case FileStatus["AVAILABLE"]:
            case FileStatus["ARCHIVED"]:
            case FileStatus["UNARCHIVED"]:
              break;
          }

          // Remove files uploading in a different session
          if (
            file.status === FileStatus.UPLOADING &&
            filesById[file.id] === undefined
          ) {
            return false;
          }

          // Remove series members
          if (file.seriesParentId !== null) {
            return false;
          }

          return true;
        })
        .map((file) => {
          assert(isNonNull(file.user));
          const baseInfo = {
            ...file,
            user: file.user,
            source: file.source as FileSource,
          };
          if (file.isSeries) {
            return {
              ...baseInfo,
              isSeries: true,
              seriesFiles: file.seriesFiles.nodes,
            };
          } else {
            return {
              ...baseInfo,
              isSeries: false,
              seriesFiles: null,
            };
          }
        })
    );
  }, [data?.project?.files.nodes, filesById]);

  const { isDraggingFiles } = useDragState();

  const { enqueueUpload, writeCache } = useUploadFile();

  const project = data?.project;
  if (isNonNullish(project) && fileBrowserFiles !== undefined) {
    const onDropFiles = (files: File[]) => {
      files.forEach((file) => {
        const dateCreated = new Date().toISOString();
        const fileId = uuid();

        const params = {
          id: fileId,
          dateCreated,
          name: file.name,
          project,
          tenantId: project.tenantId,
          size: String(file.size),
        };

        enqueueUpload({ ...params, datasetId: null, blob: file })
          .then(() => {
            writeCache({ ...params, datasetId: null });
          })
          .catch(() => {
            addUtilityToastFailure(`Failed to upload ${file.name}`);
          });
      });
      addUtilityToastSuccess(
        `Successfully queued ${files.length} files for upload.`,
      );
    };

    return (
      <>
        {modal}
        {hasEditPermission &&
          (isDraggingFiles || fileBrowserFiles.length === 0) && (
            <DropZoneBasic
              onSelectFiles={onDropFiles}
              isClickable={fileBrowserFiles.length === 0}
              css={styles.dropZone}
            />
          )}

        {(!hasEditPermission ||
          (!isDraggingFiles && fileBrowserFiles?.length > 0)) && (
          <FileBrowser
            extraColumnProps={extraColumnProps}
            extraColumns={extraColumns}
            defaultColumnOrder={[
              "name",
              "dateCreated",
              "source",
              "status",
              "user",
              "tags",
              "assignment",
              "size",
              "isSeries",
            ]}
            css={styles.fileBrowser}
            defaultSource="ALL"
            files={fileBrowserFiles}
            fileRenderer={({ file }) => (
              <CellValueRendererFile
                fileId={file.id}
                suppressContextMenu
                isDraggable
              />
            )}
            selection={{
              actions: {
                archive: baseActions.actions.archive,
                download: baseActions.actions.download,
                rename: baseActions.actions.rename,
                unarchive: baseActions.actions.unarchive,

                cancel_upload: {
                  onExecute: async (files) => {
                    const successfulFiles: typeof files = [];
                    const errors: Error[] = [];

                    for (const file of files) {
                      try {
                        await deleteFile({ fileId: file.id, mode: "delete" });
                        successfulFiles.push(file);
                      } catch (error) {
                        errors.push(error as Error);
                      }
                    }

                    return { successfulFiles, errors };
                  },
                },

                createSeries: {
                  onExecute: (files) =>
                    new Promise((resolve) => {
                      setModal(
                        <ModalCreateSeries
                          selectedFileIds={files.map(({ id }) => id)}
                          onSubmit={async ({ id, name, series_files }) => {
                            const { data, error } = await createSeries({
                              seriesFileId: id,
                              seriesFileName: name,
                              seriesMemberFileIds: series_files,
                            });

                            if (error === undefined) {
                              return data;
                            } else {
                              throw error;
                            }
                          }}
                          onComplete={(memberFiles) => {
                            resolve({
                              successfulFiles: memberFiles,
                              errors: [],
                            });
                            setModal(null);
                          }}
                          onError={(error) => {
                            resolve({ successfulFiles: [], errors: [error] });
                            setModal(null);
                          }}
                          onClose={() => {
                            resolve({ successfulFiles: [], errors: [] });
                            setModal(null);
                          }}
                        />,
                      );
                    }),
                  suppressLoading: true,
                  isDisabled: (files) =>
                    !validateSeriesFiles(
                      files.map((file) => ({
                        ...file,
                      })),
                    ).isValid,
                  disabledToolTip: (files) =>
                    validateSeriesFiles(
                      files.map((file) => ({
                        ...file,
                      })),
                    ).validationError,
                },

                delete: {
                  onExecute: (files) =>
                    new Promise((resolve) => {
                      setModal(
                        <ModalDeleteDrsFile
                          drsFiles={files.map((file) => ({
                            ...file,
                          }))}
                          onComplete={({ successfulFiles, errors }) => {
                            resolve({ successfulFiles, errors });
                            setModal(null);
                          }}
                          onClose={() => {
                            resolve({ successfulFiles: [], errors: [] });
                            setModal(null);
                          }}
                        />,
                      );
                    }),
                  suppressLoading: true,
                },

                revertSeries: {
                  onExecute: (files) =>
                    new Promise((resolve) => {
                      setModal(
                        <ModalBreakSeries
                          drsFiles={files.map(({ source, ...file }) => ({
                            ...file,
                          }))}
                          onSubmit={async (fileId) => {
                            const { data, error } = await breakSeries({
                              fileId,
                            });

                            if (error === undefined) {
                              return data;
                            } else {
                              throw error;
                            }
                          }}
                          onComplete={({ successfulFiles, errors }) => {
                            resolve({ successfulFiles, errors });
                            setModal(null);
                          }}
                          onClose={() => {
                            resolve({ successfulFiles: [], errors: [] });
                            setModal(null);
                          }}
                        />,
                      );
                    }),
                  suppressLoading: true,
                  isDisabled: (files) => !files.some((file) => file.isSeries),
                  disabledToolTip: "You must select a series to break it.",
                },

                unassign: {
                  suppressLoading: true,
                  disabledToolTip:
                    "You must select at least one assigned file.",
                  isDisabled: (files) => {
                    return !files.some((file) => {
                      const fetchedFile = fileMap.filesById[file.id];
                      return (
                        file.source === FileSource.UPLOADED &&
                        fetchedFile !== undefined &&
                        !(fetchedFile instanceof Error)
                      );
                    });
                  },
                  onExecute: (files) =>
                    new Promise((resolve) => {
                      const unassignableFiles = files.filter((file) => {
                        const fetchedFile = fileMap.filesById[file.id];
                        return (
                          file.source === FileSource.UPLOADED &&
                          fetchedFile !== undefined &&
                          !(fetchedFile instanceof Error)
                        );
                      });

                      setModal(
                        <ModalConfirmDestructiveAction
                          body={
                            <EuiText>
                              Are you sure you wish to unassign the following{" "}
                              {unassignableFiles.length} file(s):
                              <p>
                                {unassignableFiles.map(({ id, name }) => (
                                  <div key={id}>{name}</div>
                                ))}
                              </p>
                            </EuiText>
                          }
                          onConfirm={() => {
                            unassignableFiles.forEach((file) => {
                              void unassignFile({ fileId: file.id });
                            });

                            // Treat enqueuing actions as a success
                            resolve({
                              successfulFiles: unassignableFiles,
                              errors: [],
                            });

                            setModal(null);
                          }}
                          onCancel={() => {
                            resolve({ successfulFiles: [], errors: [] });
                            setModal(null);
                          }}
                        />,
                      );
                    }),
                },
              },
            }}
          />
        )}
      </>
    );
  }

  if (loading) {
    return <EuiSkeletonText lines={5} />;
  }

  return <CallOutError />;
});
